/**
 * OnRegistrationSurvey Component
 *
 * Post-registration survey component that collects user preferences and demographics
 * after email verification. This is Stage 1 implementation with UI/UX replication
 * from MVP, using TypeScript and Material UI components.
 *
 * Features:
 * - A/B testing with 50% probability split between multiselect and freeform questions
 * - Conditional "other" text fields for custom responses
 * - Form validation and error handling
 * - Navigation flow: registration → email verification → survey → dashboard
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useAuthContext } from "../contexts/AuthContext";
import { AuthLayout } from "../components/Layouts";
import { createLoginStyles, createSurveyStyles } from "./styles";
import { useTheme } from "@mui/material/styles";
import { ROUTES } from "../constants/routes";
import {
  REASONS_FOR_VISITING_DEEPDIVE,
  USER_SELF_IDENTIFIED_AS_OPTIONS,
  ReasonForVisitingDeepDive,
  UserSelfIdentifiedAs,
} from "../constants/survey";
import { useOnRegistrationSurvey } from "../hooks/useOnRegistrationSurvey";

/**
 * Survey form data structure matching MVP implementation
 * Supports both multiselect and freeform question variants
 */
interface SurveyFormData {
  /** Array of selected reason values for multiselect variant */
  reason_for_signup: string[];
  /** Custom text when "other" is selected in multiselect */
  other_signup_reason_text: string | null;
  /** Freeform text response for single text input variant */
  reason_for_signup_freeform: string;
  /** Selected user identification category */
  user_self_identified_as: string | null;
  /** Custom text when "other" is selected for user identification */
  other_user_self_identified_as_text: string | null;
}

/** Probability of showing multiselect vs freeform for A/B testing */
const PROBABILITY_OF_SHOWING_MULTI_SELECT = 0.5;

/**
 * OnRegistrationSurvey Component
 *
 * Renders the post-registration survey form with Material UI components
 * following the same patterns as Login.tsx with AuthLayout wrapper.
 */
const OnRegistrationSurvey: React.FC = () => {
  const theme = useTheme();
  const loginStyles = createLoginStyles(theme);
  const surveyStyles = createSurveyStyles(theme);

  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { submitSurvey, isSubmitting, error } = useOnRegistrationSurvey();

  const [showMultiSelect] = useState(
    Math.random() < PROBABILITY_OF_SHOWING_MULTI_SELECT
  );

  const [formData, setFormData] = useState<SurveyFormData>({
    reason_for_signup: [],
    other_signup_reason_text: null,
    reason_for_signup_freeform: "",
    user_self_identified_as: null,
    other_user_self_identified_as_text: null,
  });

  /**
   * Handles form field changes with conditional logic for "other" text fields
   *
   * @param fieldName - The form field being updated
   * @param value - The new value for the field
   */
  const handleFieldChange = (fieldName: keyof SurveyFormData, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldName]: value };

      if (
        fieldName === "reason_for_signup" &&
        (!value || !value.includes(ReasonForVisitingDeepDive.OTHER))
      ) {
        newData.other_signup_reason_text = null;
      } else if (
        fieldName === "user_self_identified_as" &&
        value !== UserSelfIdentifiedAs.OTHER
      ) {
        newData.other_user_self_identified_as_text = null;
      }

      return newData;
    });
  };

  /**
   * Handles form submission using the survey API hook
   *
   * @param e - Form submission event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await submitSurvey(formData);
      navigate(ROUTES.DASHBOARD);
    } catch (err) {}
  };

  return (
    <AuthLayout>
      <title>Register with the Materiom Commons</title>
      {/* Main Container */}
      <Container sx={loginStyles.container}>
        <Paper elevation={24} sx={loginStyles.paper}>
          {/* Header Section */}
          {/* <Typography variant="h4" component="h2" sx={loginStyles.header}>
            Welcome to Materiom!
          </Typography> */}

          <Typography variant="body2" sx={{ ...loginStyles.fieldLabel, mb: 2 }}>
            Thanks for registering! Before continuing, please take a moment to
            tell us what brought you to the Commons.
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={loginStyles.errorAlert}>
              {error}
            </Alert>
          )}

          {/* Survey Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {/* Question 1: Reason for Signup - A/B Testing Variant */}
            {showMultiSelect ? (
              /* Multiselect Variant */
              <Box sx={loginStyles.inputContainer}>
                <Typography
                  variant="body2"
                  component="label"
                  sx={loginStyles.fieldLabel}
                >
                  What are you hoping to find in the Commons? (Choose as many as
                  you'd like)
                </Typography>
                {/* Multiselect Autocomplete with Chip Display */}
                <Autocomplete
                  multiple
                  options={REASONS_FOR_VISITING_DEEPDIVE}
                  getOptionLabel={(option) => option.label}
                  value={REASONS_FOR_VISITING_DEEPDIVE.filter((option) =>
                    formData.reason_for_signup.includes(option.value)
                  )}
                  onChange={(_, newValue) => {
                    handleFieldChange(
                      "reason_for_signup",
                      newValue.map((v) => v.value)
                    );
                  }}
                  slotProps={{
                    popper: {
                      sx: surveyStyles.autocompletePopper,
                    },
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.label}
                        {...getTagProps({ index })}
                        key={option.value}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={{
                        ...loginStyles.textField,
                        ...surveyStyles.multiSelectAutocomplete,
                      }}
                      required={formData.reason_for_signup.length === 0}
                    />
                  )}
                />

                {/* Conditional "Other" Text Field */}
                {formData.reason_for_signup.includes(
                  ReasonForVisitingDeepDive.OTHER
                ) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      component="label"
                      sx={loginStyles.fieldLabel}
                    >
                      Something else? Please specify
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.other_signup_reason_text || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "other_signup_reason_text",
                          e.target.value
                        )
                      }
                      placeholder="Enter text..."
                      variant="outlined"
                      sx={loginStyles.textField}
                      required
                    />
                  </Box>
                )}
              </Box>
            ) : (
              /* Freeform Text Variant */
              <Box sx={loginStyles.inputContainer}>
                <Typography
                  variant="body2"
                  component="label"
                  sx={loginStyles.fieldLabel}
                >
                  What are you hoping to find in the Commons?
                </Typography>
                <TextField
                  fullWidth
                  value={formData.reason_for_signup_freeform}
                  onChange={(e) =>
                    handleFieldChange(
                      "reason_for_signup_freeform",
                      e.target.value
                    )
                  }
                  placeholder="Enter text..."
                  variant="outlined"
                  sx={loginStyles.textField}
                  required
                />
              </Box>
            )}

            {/* Question 2: User Self-Identification */}
            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                What best describes you?
              </Typography>
              {/* Single Select Autocomplete */}
              <Autocomplete
                options={USER_SELF_IDENTIFIED_AS_OPTIONS}
                getOptionLabel={(option) => option.label}
                value={
                  USER_SELF_IDENTIFIED_AS_OPTIONS.find(
                    (option) =>
                      option.value === formData.user_self_identified_as
                  ) || null
                }
                onChange={(_, newValue) => {
                  handleFieldChange(
                    "user_self_identified_as",
                    newValue?.value || null
                  );
                }}
                slotProps={{
                  popper: {
                    sx: surveyStyles.autocompletePopper,
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={{
                      ...loginStyles.textField,
                      ...surveyStyles.singleSelectAutocomplete,
                    }}
                    required
                  />
                )}
              />

              {/* Conditional "Other" Text Field */}
              {formData.user_self_identified_as ===
                UserSelfIdentifiedAs.OTHER && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    component="label"
                    sx={loginStyles.fieldLabel}
                  >
                    Something else? Please specify
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.other_user_self_identified_as_text || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "other_user_self_identified_as_text",
                        e.target.value
                      )
                    }
                    placeholder="Enter text..."
                    variant="outlined"
                    sx={loginStyles.textField}
                    required
                  />
                </Box>
              )}
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={loginStyles.loginButton}
              disableRipple
            >
              {isSubmitting ? "Submitting..." : "Continue"}
            </Button>
          </Box>
        </Paper>
      </Container>
    </AuthLayout>
  );
};

export default OnRegistrationSurvey;
